/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import CardReaderCircle from './CardReaderCircle'
import Button from './Button'

import PeripheralBridge from '../utils/peripheralBridge'
import { isOfflineApproval } from '../utils/offlineReplayStatuses'
import { invoiceNumber } from '../utils/orderNumbers'
import { fakeCreditCardResponse, showFakeCreditCard } from '../utils/fakeCreditCardResponse'

import { ReactComponent as AuthorizingIcon } from '../assets/icons/tabs/authorizing-icon.svg'
import { ReactComponent as FailedIcon } from '../assets/icons/tabs/failed-icon.svg'
import { ReactComponent as ApprovedIcon } from '../assets/icons/tabs/approved-icon.svg'

import styles from './CardReader.module.scss'
import { getDeviceSerial } from '../utils/formatters'
import { cardReaderApproved } from '../utils/orderUtils'

const getAuthType = (authType) => {
  switch (authType) {
    case 'sale': return 'executeSaleRequest'
    case 'auth': return 'executeAuthRequest'
    default: return 'executeAuthRequest'
  }
}

const STATUS = {
  'ready': {
    cardReaderStatusText: 'Card Reader is Ready',
    icon: null,
    buttons: showFakeCreditCard() ? ['cancelButton', 'fakeCard'] : ['cancelButton'],
  },
  'authorizing': {
    cardReaderStatusText: 'Authorizing...',
    icon: <AuthorizingIcon />,
    buttons: ['cancelButton'],
  },
  'failed': {
    cardReaderStatusText: 'Declined',
    icon: <FailedIcon />,
    buttons: ['tryAgainButton', 'cancelButton'],
  },
  'success': {
    cardReaderStatusText: 'Approved!',
    icon: <ApprovedIcon />,
    buttons: ['doneButton'],
  },
}

const CardReaderViewModel = ({ status, buttonsMap }) => {
  const { cardReaderStatusText, icon, buttons: buttonStrings } = STATUS[status]
  const buttons = buttonStrings.map((buttonString) => buttonsMap[buttonString])

  return {
    buttons,
    cardReaderStatusText,
    icon,
  }
}

const CardReader = ({ uuid, orderNumber, total, authorizationType, onCancel, onRetry, onSuccess, onFailed, onDone, className, ...props }) => {
  const classNames = cn(styles.cardReader, className)
  const [status, setStatus] = useState('ready')
  const method = getAuthType(authorizationType)

  const handleOnRetry = () => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) {
      setStatus('ready')
      onRetry()
    } else {
      const deviceSerial = getDeviceSerial()
      bridgeInstance.call(method, { 'uuid': uuid, orderNumber, 'hRef': invoiceNumber(orderNumber), 'amount': total, deviceSerial })
      setStatus('ready')
      onRetry()
    }
  }

  const onFakeCreditCardSwipe = () => {
    setStatus('success')
    onSuccess(fakeCreditCardResponse(uuid, total, authorizationType))
  }

  const onCancelTransaction = () => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) {
      onCancel()
      return
    }
    bridgeInstance.call('cancelTransactionAttempt')
    onCancel()
  }

  const viewModel = CardReaderViewModel({
    status,
    buttonsMap: {
      cancelButton: <Button key="cancel" onClick={onCancelTransaction} className={styles.actionButton}>Cancel</Button>,
      tryAgainButton: <Button key="retry" onClick={handleOnRetry} className={styles.actionButton}>Try Again</Button>,
      doneButton: <Button key="done" onClick={onDone} className={styles.actionButton}>Done</Button>,
      fakeCard: <Button key="fake" onClick={onFakeCreditCardSwipe} className={styles.actionButton}>Fake Credit Card</Button>,
    },
  })

  const { cardReaderStatusText, buttons, icon } = viewModel

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    bridgeInstance.registerHandler('handleResult', function(data, callback) {
      if (cardReaderApproved(data) && (data.transType === 'auth' || data.transType === 'sale')) {
        const dataWithUuid = { ...data, uuid }

        setStatus('success')
        onSuccess(dataWithUuid, isOfflineApproval(data))

      } else {
        setStatus('failed')
        onFailed()
      }

      var response = { 'message': data }
      callback(response)
    })

    const deviceSerial = getDeviceSerial()
    bridgeInstance.call(method, { 'uuid': uuid, orderNumber, 'hRef': invoiceNumber(orderNumber), 'amount': total, deviceSerial })
  }, [uuid, total])

  useEffect(() => () => {
    if (status === 'success') return
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return
    bridgeInstance.call('cancelTransactionAttempt')
  }, [])

  return (
    <div {...props} className={classNames}>
      <CardReaderCircle className={styles.cardReaderCircle} icon={icon} />
      <p className={styles.cardReaderStatusText}>{cardReaderStatusText}</p>
      <div className={styles.buttonContainer}>
        {buttons}
      </div>
    </div>
  )
}

CardReader.defaultProps = {
  uuid: '',
  total: '',
  authorizationType: 'sale',
  onCancel: () => {},
  onRetry: () => {},
  onSuccess: () => {},
  onFailed: () => {},
  onDone: () => {},
}

CardReader.propTypes = {
  uuid: PropTypes.string,
  total: PropTypes.string,
  authorizationType: PropTypes.oneOf(['sale', 'auth']),
  onCancel: PropTypes.func,
  onRetry: PropTypes.func,
  onSuccess: PropTypes.func,
  onFailed: PropTypes.func,
  onDone: PropTypes.func,
}

export default CardReader
